import { z } from 'zod';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import { McpMultiIdentityDiscoveryResultSchema } from '@terra/shared-contracts-mcp-servers';
import {
  AiApplicationListQuerySchema,
  AiApplicationListResponseSchema,
  AiApplicationStatsSchema,
  AiApplicationSchema,
  AiDiscoverySummarySchema,
  AiDiscoveryTriggerResponseSchema,
  AiDiscoveryStopResponseSchema,
  AiDiscoveryProbeRetryResponseSchema,
  AiComponentReconciliationReportSchema,
  MergeAiComponentsSchema,
  MergeAiComponentsResponseSchema,
  CreateAiComponentSchema,
  UpdateAiComponentSchema,
  AiComponentSchema,
  CreateAiApplicationSchema,
  UpdateAiApplicationSchema,
  DeleteAiApplicationResponseSchema,
  AiApplicationUrlEditRestrictionSchema,
  CopyAiApplicationFromWebApplicationResponseSchema,
} from './ai-applications.schema';
import {
  AiApplicationComponentsListResponseSchema,
  AiComponentsListQuerySchema,
} from './ai-component-list.schema';
import { AI_APPLICATIONS_PATH_NAME } from './ai-applications.constants';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';

export const TestMcpConnectionBodySchema = z.object({
  identityId: z.string().min(1).optional(),
});

export const AiApplicationsContract = terraContract
  .prefix(`/${AI_APPLICATIONS_PATH_NAME}`)
  .router({
    list: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List AI applications',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: AiApplicationListQuerySchema }))
      .output(AiApplicationListResponseSchema),
    getById: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{aiApplicationId}',
        summary: 'Get AI application by ID',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiApplicationSchema),
    create: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create AI application',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateAiApplicationSchema }))
      .output(AiApplicationSchema),
    update: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{aiApplicationId}',
        summary: 'Update AI application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ aiApplicationId: z.string() }),
          body: UpdateAiApplicationSchema,
        }),
      )
      .output(AiApplicationSchema),
    delete: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{aiApplicationId}',
        summary: 'Delete AI application',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(DeleteAiApplicationResponseSchema),
    listComponents: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{aiApplicationId}/components',
        summary: 'List AI application components',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ aiApplicationId: z.string() }),
          query: AiComponentsListQuerySchema.optional(),
        }),
      )
      .output(AiApplicationComponentsListResponseSchema),
    getStats: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{aiApplicationId}/stats',
        summary: 'Get AI application stats',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiApplicationStatsSchema),
    getUrlEditRestriction: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{aiApplicationId}/url-edit-restriction',
        summary: 'Check AI application URL edit restriction',
        description:
          'Checks if the AI application URL can be edited. URL editing is restricted after discovery has started and assets have been created.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiApplicationUrlEditRestrictionSchema),
    discover: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/discover',
        summary: 'Trigger AI application discovery',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiDiscoveryTriggerResponseSchema),
    stopDiscovery: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/stop-discovery',
        summary: 'Stop running AI application discovery',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiDiscoveryStopResponseSchema),
    retryDiscoveryProbe: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/discovery/probes/{candidateId}/retry',
        summary: 'Retry a single AI discovery probe',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            aiApplicationId: MongoObjectIdSchema,
            candidateId: MongoObjectIdSchema,
          }),
        }),
      )
      .output(AiDiscoveryProbeRetryResponseSchema),
    testMcpConnection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/test-mcp-connection',
        summary:
          'Test MCP server connection for all configured identities, or a single identity when identityId is provided',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ aiApplicationId: z.string() }),
          body: TestMcpConnectionBodySchema,
        }),
      )
      .output(McpMultiIdentityDiscoveryResultSchema),
    discoverySummary: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{aiApplicationId}/discovery-summary',
        summary: 'Get AI application discovery summary',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiDiscoverySummarySchema),
    getComponentReconciliation: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{aiApplicationId}/components/reconciliation',
        summary: 'Get AI component reconciliation report',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ aiApplicationId: z.string() }) }))
      .output(AiComponentReconciliationReportSchema),
    mergeComponents: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/components/merge',
        summary: 'Merge AI application components',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ aiApplicationId: z.string() }),
          body: MergeAiComponentsSchema,
        }),
      )
      .output(MergeAiComponentsResponseSchema),
    createComponent: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/components',
        summary: 'Create a manual AI component',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ aiApplicationId: z.string() }),
          body: CreateAiComponentSchema,
        }),
      )
      .output(AiComponentSchema),
    updateComponent: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{aiApplicationId}/components/{componentId}',
        summary: 'Update an AI component',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            aiApplicationId: z.string(),
            componentId: z.string(),
          }),
          body: UpdateAiComponentSchema,
        }),
      )
      .output(AiComponentSchema),
    copyFromWebApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{aiApplicationId}/copy-from-web-application/{webApplicationId}',
        summary: 'Copy settings from a web application into an AI application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            aiApplicationId: MongoObjectIdSchema,
            webApplicationId: MongoObjectIdSchema,
          }),
        }),
      )
      .output(CopyAiApplicationFromWebApplicationResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
        FORBIDDEN: { status: 403, data: z.object({ message: z.string() }) },
      }),
  });
